import {Dialog, Heading, Pane, PlusIcon} from 'evergreen-ui';
import React, {useCallback, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useObservable} from 'rxsupport';
import {NewProviderUserMutationInput} from '../domain/types';
import { USER_POPUP_HEIGHT } from './const';
import {CancelButton, OkButton} from './ProviderUserForm/component/FormButtons';
import {NewUserFormContent} from './ProviderUserForm/component/FormContents';
import {useNewProviderUserMutation$} from './ProviderUserForm/hook/useNewProviderUserMutation$';
import {UserFormInput} from './ProviderUserForm/types';

const useValidatedNewUser = (successCallback: () => void) => {
    const {submit, isRunning$} = useNewProviderUserMutation$(successCallback);
    const methods = useForm<UserFormInput, object>({
        defaultValues: {
            email: null,
            firstName: null,
            lastName: null,
            clientPlacementAccess: false,
            clientReportAccess: false,
            providerMetricsAccess: false,
            rosterAccess: false,
            auditLogAccess: false,
            contactEditAccess: false,
            contactReadAccess: true,
            userManagementAdminAccess: false,
        },
    });
    const {trigger, getValues} = methods;
    const createNewUser = useCallback(async () => {
        const isValid = await trigger();
        if (isValid) {
            await submit(getValues() as NewProviderUserMutationInput);
        }
    }, [trigger, getValues, submit]);

    return {
        methods,
        isSubmitting$: isRunning$,
        submit: createNewUser,
    }
};
type Prop = {
    onSuccess?: () => Promise<void>;
}
export const NewUserPopup = ({ onSuccess }: Prop) => {
    const [showPopup, setShowPopup] = useState(false);
    const closePopup = useCallback(() => {
        setShowPopup(false);
    }, []);
    const successCallback = useCallback(() => {
        setShowPopup(false);
        if (onSuccess) {
            onSuccess();
        }
    }, [onSuccess]);

    const {isSubmitting$, submit, methods} = useValidatedNewUser(successCallback);

    const loading = useObservable(isSubmitting$, false);

    return (
        <>
            <button
                onClick={() => setShowPopup(true)}
                disabled={loading}
                className='bg-white h-10 w-32 my-4 py-2 px-1 text-atlantis-200 border
                hover:bg-gray-200 border-atlantis-200 font-semibold disabled:opacity-50
                text-sm focus:outline-none focus:ring focus:border-atlantis-300 rounded'>
                <PlusIcon display={'inline'} className='pr-1'/> New User
            </button>
            <Dialog
                isShown={showPopup}
                hasHeader={false}
                hasFooter={false}
                width={400}
                containerProps={ {
                    maxHeight: USER_POPUP_HEIGHT,
                    marginY: `calc((100vh - ${ USER_POPUP_HEIGHT }px) / 2)`,
                    overflowY: 'auto'
                } }
                onCloseComplete={closePopup}
            >
                {({close}) => (
                    <>
                        <Heading size={600} textAlign='center' marginY='16px'>Create New User</Heading>
                        <FormProvider {...methods}>
                            <form className='flex flex-col items-center'>
                                <NewUserFormContent/>
                                <Pane display='flex' justifyContent='center'
                                      paddingTop={12} paddingLeft={24} width='100%'
                                >
                                    <CancelButton isSubmitting$={isSubmitting$} cancel={close}/>
                                    <OkButton text='Create' isSubmitting$={isSubmitting$} submit={submit}/>
                                </Pane>
                            </form>
                        </FormProvider>
                    </>
                )}
            </Dialog>
        </>
    );
};
